import { useEffect } from 'react';

import { COLLAPSE_DY_PX, EXPAND_DY_PX } from './constants';
import type { UseSearchDrawerDragParams } from './types';

// Mobile-only peek <-> expand drag for the search drawer. Pointer-capture
// pattern mirrors useSheetDrag (AirspaceSheet), but the search drawer never
// dismisses — it's the default mode — so there's no "swipe down past peek =
// gone" behavior.
const useSearchDrawerDrag = ({
    dragHandleRef,
    isEnabled,
    onCollapse,
    onExpand
}: UseSearchDrawerDragParams): void => {
    useEffect(() => {
        const handle = dragHandleRef.current;

        if (!isEnabled || handle === null) {
            return undefined;
        }

        let startY: number | null = null;
        let currentDy = 0;

        const handlePointerDown = (event: PointerEvent) => {
            startY = event.clientY;
            currentDy = 0;
            handle.setPointerCapture(event.pointerId);
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (startY === null) {
                return;
            }

            currentDy = event.clientY - startY;
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (startY === null) {
                return;
            }

            const dy = currentDy;

            if (handle.hasPointerCapture(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }

            startY = null;
            currentDy = 0;

            if (dy < EXPAND_DY_PX) {
                onExpand();

                return;
            }

            if (dy > COLLAPSE_DY_PX) {
                onCollapse();
            }
        };

        const handlePointerCancel = (event: PointerEvent) => {
            if (handle.hasPointerCapture(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }

            startY = null;
            currentDy = 0;
        };

        handle.addEventListener('pointerdown', handlePointerDown);
        handle.addEventListener('pointermove', handlePointerMove);
        handle.addEventListener('pointerup', handlePointerUp);
        handle.addEventListener('pointercancel', handlePointerCancel);

        return () => {
            handle.removeEventListener('pointerdown', handlePointerDown);
            handle.removeEventListener('pointermove', handlePointerMove);
            handle.removeEventListener('pointerup', handlePointerUp);
            handle.removeEventListener('pointercancel', handlePointerCancel);
        };
    }, [dragHandleRef, isEnabled, onCollapse, onExpand]);
};

export default useSearchDrawerDrag;
